/* ==========================================================================
   Simpatico V2 — header interactions
   Mobile drawer, support sheet, desktop popouts and the focused-search state.

   Everything the prototype left open is handled here: focus trapping, Esc,
   focus restoration, aria-expanded wiring and click-outside. Motion itself is
   CSS, so prefers-reduced-motion is honoured by the stylesheet.

   No dependencies. Safe to load deferred and to re-run on section reload.
   ========================================================================== */
(function () {
  'use strict';

  var FOCUSABLE = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])'
  ].join(',');

  /* -- Body scroll lock -------------------------------------------------- */
  /* Reference-counted so a sheet opened from the drawer cannot unlock early. */
  var lockCount = 0;

  function lockScroll() {
    if (lockCount === 0) document.body.classList.add('s2-locked');
    lockCount++;
  }

  function unlockScroll() {
    lockCount = Math.max(0, lockCount - 1);
    if (lockCount === 0) document.body.classList.remove('s2-locked');
  }

  /* -- Focus trap -------------------------------------------------------- */
  function visibleFocusable(container) {
    return Array.prototype.filter.call(
      container.querySelectorAll(FOCUSABLE),
      function (el) { return el.offsetWidth > 0 || el.offsetHeight > 0 || el === document.activeElement; }
    );
  }

  function trapFocus(container, event) {
    var items = visibleFocusable(container);
    if (!items.length) return;

    var first = items[0];
    var last = items[items.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  /* -- Overlay (drawer / sheet) ------------------------------------------ */
  function Overlay(panel, options) {
    this.panel = panel;
    this.options = options || {};
    this.isOpen = false;
    this.returnFocusTo = null;
    this.onKeydown = this.handleKeydown.bind(this);
  }

  Overlay.prototype.open = function (trigger) {
    if (this.isOpen) return;
    this.isOpen = true;
    this.returnFocusTo = trigger || document.activeElement;
    this.panel.setAttribute('data-open', 'true');
    this.panel.removeAttribute('aria-hidden');
    if (trigger) trigger.setAttribute('aria-expanded', 'true');
    lockScroll();
    document.addEventListener('keydown', this.onKeydown);

    var target = this.panel.querySelector('[data-s2-autofocus]') || visibleFocusable(this.panel)[0];
    if (target) target.focus();
  };

  Overlay.prototype.close = function () {
    if (!this.isOpen) return;
    this.isOpen = false;
    this.panel.setAttribute('data-open', 'false');
    this.panel.setAttribute('aria-hidden', 'true');
    unlockScroll();
    document.removeEventListener('keydown', this.onKeydown);

    if (this.options.trigger) this.options.trigger.setAttribute('aria-expanded', 'false');
    // Only restore focus if it is still inside the panel we are closing —
    // otherwise we would yank focus away from wherever the user moved on to.
    if (this.returnFocusTo && this.panel.contains(document.activeElement)) {
      this.returnFocusTo.focus();
    }
    this.returnFocusTo = null;
  };

  Overlay.prototype.handleKeydown = function (event) {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
    } else if (event.key === 'Tab') {
      trapFocus(this.panel, event);
    }
  };

  /* -- Desktop popouts --------------------------------------------------- */
  /* Open on hover and focus; close on leave (with grace), Esc or click-out.
     Only one may be open at a time. */
  function PopoutGroup(root) {
    this.root = root;
    this.items = [];
    this.openItem = null;
    this.closeTimer = null;

    var self = this;
    Array.prototype.forEach.call(root.querySelectorAll('[data-s2-popout-trigger]'), function (trigger) {
      var id = trigger.getAttribute('data-s2-popout-trigger');
      var panel = root.querySelector('[data-s2-popout="' + id + '"]');
      if (!panel) return;

      var item = { trigger: trigger, panel: panel };
      self.items.push(item);

      // Hover intent on the trigger and the panel keeps the pair alive.
      [trigger, panel].forEach(function (el) {
        el.addEventListener('mouseenter', function () { self.cancelClose(); self.show(item); });
        el.addEventListener('mouseleave', function () { self.scheduleClose(); });
      });

      // focusin (not focus) so tabbing onto a link *inside* a trigger — the
      // used-gear chip wraps two real links — also opens the panel.
      trigger.addEventListener('focusin', function () { self.cancelClose(); self.show(item); });

      trigger.addEventListener('click', function (event) {
        // Never swallow a click on a real link inside the trigger.
        if (event.target.closest('a[href]')) return;
        event.preventDefault();
        if (self.openItem === item) self.hide(); else self.show(item);
      });
      panel.addEventListener('focusin', function () { self.cancelClose(); });
    });

    document.addEventListener('keydown', function (event) {
      if (event.key === 'Escape' && self.openItem) {
        var trigger = self.openItem.trigger;
        self.hide();
        trigger.focus();
      }
    });

    document.addEventListener('click', function (event) {
      if (self.openItem && !self.root.contains(event.target)) self.hide();
    });

    // A panel losing focus entirely (tabbing past its last link) should close it.
    root.addEventListener('focusout', function (event) {
      if (!self.openItem) return;
      var next = event.relatedTarget;
      if (next && (self.openItem.panel.contains(next) || self.openItem.trigger === next)) return;
      self.hide();
    });
  }

  PopoutGroup.prototype.show = function (item) {
    if (this.openItem === item) return;
    this.hide();
    this.openItem = item;
    item.panel.setAttribute('data-open', 'true');
    item.trigger.setAttribute('aria-expanded', 'true');
    if (item.trigger.hasAttribute('data-s2-chip')) item.trigger.setAttribute('data-open', 'true');
  };

  PopoutGroup.prototype.hide = function () {
    if (!this.openItem) return;
    this.openItem.panel.setAttribute('data-open', 'false');
    this.openItem.trigger.setAttribute('aria-expanded', 'false');
    if (this.openItem.trigger.hasAttribute('data-s2-chip')) this.openItem.trigger.setAttribute('data-open', 'false');
    this.openItem = null;
  };

  PopoutGroup.prototype.scheduleClose = function () {
    var self = this;
    this.cancelClose();
    this.closeTimer = window.setTimeout(function () { self.hide(); }, 150);
  };

  PopoutGroup.prototype.cancelClose = function () {
    if (this.closeTimer) {
      window.clearTimeout(this.closeTimer);
      this.closeTimer = null;
    }
  };

  /* -- Sticky offset ------------------------------------------------------ */
  /* The section wrapper is what sticks; it is pulled up by the height of
     whatever sits above the bar so that band scrolls away while the bar pins.
     Measured rather than hard-coded because the trust bar and banner swap at
     900px, either can be switched off, and the trust bar wraps on narrow
     screens. */
  function stickyOffset(header) {
    var section = header.closest('[id^="shopify-section"]') || header.parentElement;
    var bar = header.querySelector('.s2-bar');
    if (!section || !bar) return;

    var last = null;
    var lastBar = null;

    function measure() {
      var offset = Math.max(0, Math.round(
        bar.getBoundingClientRect().top - section.getBoundingClientRect().top
      ));
      if (offset !== last) {
        last = offset;
        section.style.setProperty('--s2-preheader-h', offset + 'px');
      }

      // What stays on screen once the pre-header scrolls away is the bar itself.
      // Legacy stickies (collection toolbar, sub-collection nav, PDP gallery,
      // sidebar, sticky ATC) all offset by --header-height; the V2 header never
      // published it, so they pinned to y=0 and slid under the bar.
      var barH = bar.getBoundingClientRect().height;
      if (barH !== lastBar) {
        lastBar = barH;
        document.documentElement.style.setProperty('--header-height', barH + 'px');
      }
    }

    function sync() {
      measure();
      // Breakpoint swaps and font loading settle after the event that triggered
      // them, so re-measure once layout has actually landed. Timers rather than
      // rAF: a backgrounded or non-compositing tab never runs animation frames.
      window.setTimeout(measure, 0);
      window.setTimeout(measure, 150);
    }

    sync();
    window.addEventListener('resize', sync);

    // The offset is only consumed while scrolling, so verifying it there makes
    // the whole thing self-correcting — orientation changes, late font swaps and
    // anything the events below miss get picked up within a fifth of a second.
    // Time-throttled rather than rAF-throttled so it cannot latch off.
    var lastCheck = 0;
    window.addEventListener('scroll', function () {
      var now = Date.now();
      if (now - lastCheck < 200) return;
      lastCheck = now;
      measure();
    }, { passive: true });

    // The trust bar and banner swap here, which changes the offset. Listening
    // for the breakpoint directly is more dependable than inferring it from a
    // resize event.
    var swap = window.matchMedia('(min-width: 900px)');
    if (swap.addEventListener) swap.addEventListener('change', sync);
    else if (swap.addListener) swap.addListener(sync);

    // Also catches changes the events miss: editor edits, late images, reflow.
    // Setting `top` cannot change the observed box, so this cannot feed back.
    if (typeof ResizeObserver !== 'undefined') new ResizeObserver(sync).observe(header);
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(sync);
  }

  /* -- Predictive search -------------------------------------------------- */
  /* Wraps Shopify's native /search/suggest.json. Both the mobile command row
     and the desktop bar drive one shared panel — only one is ever visible.

     Results are built with DOM APIs rather than innerHTML: product titles are
     merchant data and must never be interpolated into markup. */
  function SearchPanel(header) {
    var panel = header.querySelector('[data-s2-search-panel]');
    var forms = header.querySelectorAll('[data-s2-search]');
    if (!panel || !forms.length) return;

    var cfg = {};
    try { cfg = JSON.parse(panel.getAttribute('data-config') || '{}'); } catch (e) { cfg = {}; }

    var sugEl = panel.querySelector('[data-s2-search-suggestions]');
    var prodEl = panel.querySelector('[data-s2-search-products]');
    var timer = null;
    var inflight = null;
    var activeIndex = -1;

    var brands = cfg.brands || [];
    var gear = cfg.gear || [];

    var formatter = null;
    try {
      formatter = new Intl.NumberFormat(cfg.locale || undefined, {
        style: 'currency',
        currency: cfg.currency || 'USD'
      });
    } catch (e) { /* fall through to a plain number below */ }

    function money(value) {
      var n = parseFloat(value);
      if (isNaN(n)) return '';
      return formatter ? formatter.format(n) : n.toFixed(2);
    }

    function el(tag, cls, text) {
      var node = document.createElement(tag);
      if (cls) node.className = cls;
      if (text != null) node.textContent = text;
      return node;
    }

    function links() {
      return Array.prototype.slice.call(panel.querySelectorAll('a'));
    }

    function setActive(index) {
      var items = links();
      if (!items.length) return;
      items.forEach(function (a) { a.removeAttribute('data-active'); });
      activeIndex = (index + items.length) % items.length;
      var target = items[activeIndex];
      target.setAttribute('data-active', 'true');
      if (target.scrollIntoView) target.scrollIntoView({ block: 'nearest' });
    }

    function openPanel() {
      panel.setAttribute('data-open', 'true');
      // A popout and the search panel must never occupy the bar together.
      if (header.__s2popouts) header.__s2popouts.hide();
    }

    function closePanel() {
      panel.setAttribute('data-open', 'false');
      activeIndex = -1;
    }

    function reset() {
      closePanel();
      header.removeAttribute('data-search-open');
    }

    function render(data, query) {
      var r = (data && data.resources && data.resources.results) || {};
      sugEl.innerHTML = '';
      prodEl.innerHTML = '';
      activeIndex = -1;

      /* --- Suggestions --- */
      sugEl.appendChild(el('p', 's2-group-label', cfg.suggestions_label || 'Suggestions'));

      function suggestion(item, qualifier, extraClass) {
        var a = el('a', 's2-search__row' + (extraClass ? ' ' + extraClass : ''));
        a.href = item.url;
        a.appendChild(el('span', null, item.title));
        if (qualifier) a.appendChild(el('span', 's2-search__qual', '— ' + qualifier));
        sugEl.appendChild(a);
      }

      (r.collections || []).forEach(function (c) {
        var kind = brands.indexOf(c.handle) > -1
          ? (cfg.brand_label || 'brand')
          : (gear.indexOf(c.handle) > -1 ? (cfg.gear_label || 'category') : (cfg.collection_label || 'collection'));
        suggestion(c, kind);
      });
      (r.pages || []).forEach(function (p) { suggestion(p, cfg.page_label || 'page'); });
      (r.articles || []).forEach(function (a) { suggestion(a, cfg.article_label || 'guide'); });

      // Always offer the pre-owned catalogue — it is one tap from any query.
      if (cfg.preowned_label) {
        suggestion(
          {
            title: query,
            url: cfg.search_url + '?q=' + encodeURIComponent(query + ' ' + (cfg.preowned_suffix || 'used'))
          },
          cfg.preowned_label,
          's2-search__row--cpo'
        );
      }

      /* --- Products --- */
      var products = (r.products || []).slice(0, cfg.product_limit || 3);
      var head = el('div', 's2-search__group-head');
      head.appendChild(el('p', 's2-group-label', cfg.products_label || 'Products'));

      if (products.length) {
        var all = el('a', 's2-link-accent', (cfg.see_all_label || 'See all results') + ' →');
        all.href = cfg.search_url + '?q=' + encodeURIComponent(query);
        head.appendChild(all);
      }
      prodEl.appendChild(head);

      if (!products.length) {
        prodEl.appendChild(el('p', 's2-search__msg', cfg.empty_text || 'No products matched. Press Enter to search everything.'));
        return;
      }

      var grid = el('div', 's2-search__prods');
      products.forEach(function (p) {
        var a = el('a', 's2-search__prod');
        a.href = p.url;

        var plate = el('span', 's2-plate');
        var src = p.featured_image && p.featured_image.url ? p.featured_image.url : p.image;
        if (src) {
          var img = document.createElement('img');
          // Ask the CDN for a right-sized crop instead of the full-size original.
          img.src = src.indexOf('?') > -1 ? src + '&width=300' : src + '?width=300';
          img.alt = '';
          img.loading = 'lazy';
          plate.appendChild(img);
        }
        a.appendChild(plate);
        a.appendChild(el('span', 's2-search__prod-title', p.title));
        a.appendChild(el('span', 's2-search__prod-price', money(p.price)));
        grid.appendChild(a);
      });
      prodEl.appendChild(grid);
    }

    function request(query) {
      if (inflight) inflight.abort();
      inflight = typeof AbortController !== 'undefined' ? new AbortController() : null;

      // Without an explicit `fields` list Shopify matches on title only, which
      // misses "simagic"/"pedals" style queries against vendor and type.
      var url = cfg.suggest_url
        + '?q=' + encodeURIComponent(query)
        + '&resources[type]=' + encodeURIComponent(cfg.types || 'product,collection,page,article')
        + '&resources[limit]=' + (cfg.resource_limit || 4)
        + '&resources[options][unavailable_products]=last'
        + '&resources[options][fields]=' + encodeURIComponent(cfg.fields || 'title,product_type,variants.title,vendor');

      fetch(url, {
        headers: { Accept: 'application/json' },
        signal: inflight ? inflight.signal : undefined
      })
        .then(function (res) { return res.ok ? res.json() : null; })
        .then(function (data) {
          if (!data) return;
          render(data, query);
          openPanel();
        })
        .catch(function () { /* aborted or offline — leave the last result up */ });
    }

    function onInput(input) {
      var query = input.value.trim();
      window.clearTimeout(timer);

      if (query.length < (cfg.min_chars || 2)) {
        closePanel();
        return;
      }
      timer = window.setTimeout(function () { request(query); }, cfg.debounce || 150);
    }

    Array.prototype.forEach.call(forms, function (form) {
      var input = form.querySelector('input[type="search"]');
      if (!input) return;

      input.addEventListener('focus', function () {
        header.setAttribute('data-search-open', 'true');
        if (input.value.trim().length >= (cfg.min_chars || 2)) onInput(input);
      });

      input.addEventListener('input', function () { onInput(input); });

      input.addEventListener('keydown', function (event) {
        if (event.key === 'Escape') {
          event.preventDefault();
          reset();
          input.blur();
        } else if (event.key === 'ArrowDown') {
          event.preventDefault();
          setActive(activeIndex + 1);
        } else if (event.key === 'ArrowUp') {
          event.preventDefault();
          setActive(activeIndex - 1);
        } else if (event.key === 'Enter' && activeIndex > -1) {
          // Only hijack Enter when a result is highlighted; otherwise let the
          // form submit through to the full search page.
          var items = links();
          if (items[activeIndex]) {
            event.preventDefault();
            window.location.href = items[activeIndex].href;
          }
        }
      });
    });

    document.addEventListener('click', function (event) {
      if (!header.contains(event.target)) reset();
    });

    header.addEventListener('focusout', function (event) {
      var next = event.relatedTarget;
      if (next && header.contains(next)) return;
      // A click on a result fires focusout before navigation; defer so the
      // browser can follow the link first.
      window.setTimeout(function () {
        if (!header.contains(document.activeElement)) reset();
      }, 0);
    });

    // Let other header controls dismiss the panel (the drawer, notably).
    header.__s2search = { reset: reset };
  }

  /* -- Proof-row quote rotator -------------------------------------------- */
  /* Each quote rises in quickly, drifts very slowly while it is read, then
     whips out as the next one arrives. The motion itself is CSS; this only
     decides whose turn it is. */
  function ProofRotator(root) {
    var quotes = root.querySelectorAll('.s2-proof__quote');
    if (quotes.length < 2) return;

    var interval = parseInt(root.getAttribute('data-interval'), 10) || 3000;
    var index = 0;
    var timer = null;

    function advance() {
      var current = quotes[index];
      index = (index + 1) % quotes.length;
      var next = quotes[index];

      current.classList.remove('is-live');
      current.classList.add('is-out');
      window.setTimeout(function () { current.classList.remove('is-out'); }, 300);

      // Restart the animation from the top on a quote that has run before.
      next.classList.remove('is-live');
      void next.offsetWidth;
      next.classList.add('is-live');
    }

    function start() { if (!timer) timer = window.setInterval(advance, interval); }
    function stop() { window.clearInterval(timer); timer = null; }

    // Hold still while someone is reading, and don't churn in a hidden tab.
    root.addEventListener('mouseenter', stop);
    root.addEventListener('mouseleave', start);
    root.addEventListener('focusin', stop);
    root.addEventListener('focusout', start);
    document.addEventListener('visibilitychange', function () {
      if (document.hidden) stop(); else start();
    });

    start();
  }

  /* -- Cart count -------------------------------------------------------- */
  /* Keeps every rendered count badge in step after an add-to-cart. */
  function refreshCartCount() {
    fetch(window.Shopify && window.Shopify.routes && window.Shopify.routes.root
      ? window.Shopify.routes.root + 'cart.js'
      : '/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (res) { return res.ok ? res.json() : null; })
      .then(function (cart) {
        if (!cart) return;
        Array.prototype.forEach.call(document.querySelectorAll('[data-s2-cart-count]'), function (el) {
          el.textContent = cart.item_count;
          el.hidden = cart.item_count === 0;
        });
      })
      .catch(function () { /* a stale badge is not worth surfacing an error for */ });
  }

  /* -- Init -------------------------------------------------------------- */
  function init(scope) {
    var root = scope || document;

    Array.prototype.forEach.call(root.querySelectorAll('[data-s2-header]'), function (header) {
      if (header.hasAttribute('data-s2-ready')) return;
      header.setAttribute('data-s2-ready', 'true');

      /* Drawer */
      var drawer = header.querySelector('[data-s2-drawer]');
      var burger = header.querySelector('[data-s2-drawer-open]');

      if (drawer && burger) {
        var drawerOverlay = new Overlay(drawer, { trigger: burger });

        burger.addEventListener('click', function () {
          // The drawer owns the screen — never leave stale results behind it.
          if (header.__s2search) header.__s2search.reset();
          drawerOverlay.open(burger);
        });

        Array.prototype.forEach.call(drawer.querySelectorAll('[data-s2-drawer-close]'), function (el) {
          el.addEventListener('click', function () { drawerOverlay.close(); });
        });

        // Any navigation out of the drawer should close it first.
        drawer.addEventListener('click', function (event) {
          var link = event.target.closest('a[href]');
          if (link && !link.hasAttribute('data-s2-keep-open')) drawerOverlay.close();
        });

        // The drawer is mobile-only; if the viewport crosses into desktop while
        // it is open we would otherwise leave the body scroll-locked.
        var desktop = window.matchMedia('(min-width: 900px)');
        var onChange = function (event) { if (event.matches) drawerOverlay.close(); };
        if (desktop.addEventListener) desktop.addEventListener('change', onChange);
        else if (desktop.addListener) desktop.addListener(onChange);
      }

      /* Desktop popouts */
      if (header.querySelector('[data-s2-popout-trigger]')) {
        header.__s2popouts = new PopoutGroup(header);
      }

      /* Predictive search */
      SearchPanel(header);

      /* Sticky header offset */
      stickyOffset(header);
    });

    /* Proof-row rotator */
    Array.prototype.forEach.call(root.querySelectorAll('[data-s2-proof-rotator]'), function (rotator) {
      if (rotator.hasAttribute('data-s2-ready')) return;
      rotator.setAttribute('data-s2-ready', 'true');
      ProofRotator(rotator);
    });

    /* Support sheet — its own section, may render outside the header. */
    Array.prototype.forEach.call(root.querySelectorAll('[data-s2-support]'), function (support) {
      if (support.hasAttribute('data-s2-ready')) return;
      support.setAttribute('data-s2-ready', 'true');

      var sheet = support.querySelector('[data-s2-sheet]');
      var pill = support.querySelector('[data-s2-sheet-open]');
      if (!sheet || !pill) return;

      var sheetOverlay = new Overlay(sheet, { trigger: pill });
      pill.addEventListener('click', function () { sheetOverlay.open(pill); });

      Array.prototype.forEach.call(sheet.querySelectorAll('[data-s2-sheet-close]'), function (el) {
        el.addEventListener('click', function () { sheetOverlay.close(); });
      });

      // Tapping a contact row hands off to the OS — close behind it.
      sheet.addEventListener('click', function (event) {
        if (event.target.closest('a[href]')) sheetOverlay.close();
      });

      /* -- Swipe to dismiss --------------------------------------------------
         The grab handle promises drag-to-dismiss, so it has to actually do it.
         Bottom sheet only; at desktop this is a centred modal with no handle. */
      var panel = sheet.querySelector('.s2-sheet__panel');
      var dragZone = sheet.querySelector('[data-s2-sheet-drag]');

      if (panel && dragZone && window.PointerEvent) {
        var dragging = false;
        var startY = 0;
        var startedAt = 0;
        var offset = 0;

        var isBottomSheet = function () {
          return !window.matchMedia('(min-width: 1200px)').matches;
        };

        var settle = function (shouldClose) {
          panel.classList.remove('s2-sheet__panel--dragging');
          panel.classList.add('s2-sheet__panel--settling');
          panel.style.transform = shouldClose ? 'translateY(100%)' : '';

          window.setTimeout(function () {
            panel.classList.remove('s2-sheet__panel--settling');
            // Close first, then clear the transform, so the panel is already
            // hidden and never flashes back up for a frame.
            if (shouldClose) sheetOverlay.close();
            panel.style.transform = '';
          }, shouldClose ? 190 : 210);
        };

        dragZone.addEventListener('pointerdown', function (event) {
          if (!isBottomSheet() || event.button > 0) return;
          dragging = true;
          startY = event.clientY;
          startedAt = Date.now();
          offset = 0;
          panel.classList.add('s2-sheet__panel--dragging');
          try { dragZone.setPointerCapture(event.pointerId); } catch (e) { /* older Safari */ }
        });

        dragZone.addEventListener('pointermove', function (event) {
          if (!dragging) return;
          // Downward only — dragging up must not lift the sheet off the edge.
          offset = Math.max(0, event.clientY - startY);
          panel.style.transform = 'translateY(' + offset + 'px)';
        });

        var release = function () {
          if (!dragging) return;
          dragging = false;
          var elapsed = Math.max(Date.now() - startedAt, 1);
          var velocity = offset / elapsed;
          // Dragged past a third of the sheet, or flicked down quickly.
          settle(offset > panel.offsetHeight * 0.3 || (velocity > 0.5 && offset > 40));
        };

        dragZone.addEventListener('pointerup', release);
        dragZone.addEventListener('pointercancel', release);
      }

      /* -- Live chat, adopted into the sheet --------------------------------
         Shopify Inbox renders a <shopify-chat> element with an open shadow
         root containing button.activator. We hide that launcher and drive it
         from a row in this sheet, so the page has one floating action instead
         of two competing ones.

         Everything here is fail-safe: the row stays hidden and Inbox keeps its
         own button unless we positively identify the launcher. If Shopify
         changes the internals, the site degrades to today's behaviour rather
         than losing chat entirely. */
      var chatRow = support.querySelector('[data-s2-chat-row]');

      if (chatRow && support.getAttribute('data-s2-chat-merge') === 'true') {
        var chatSelector = support.getAttribute('data-s2-chat-selector') || 'shopify-chat';

        // Inbox nests its launcher two shadow roots deep
        // (shopify-chat -> shopify-agent-runtime -> button.activator), and the
        // depth has changed between renders, so search rather than hard-code a
        // path.
        var deepQuery = function (root, selector, depth) {
          if (!root || depth > 5) return null;
          var direct = root.querySelector(selector);
          if (direct) return direct;
          var nodes = root.querySelectorAll('*');
          for (var i = 0; i < nodes.length; i++) {
            if (nodes[i].shadowRoot) {
              var hit = deepQuery(nodes[i].shadowRoot, selector, depth + 1);
              if (hit) return hit;
            }
          }
          return null;
        };

        var findActivator = function () {
          var host = document.querySelector(chatSelector);
          if (!host || !host.shadowRoot) return null;
          return deepQuery(host.shadowRoot, 'button.activator, [part~="activator"]', 0);
        };

        // Hides the launcher and its "Contact" nudge bubble. Opacity and
        // pointer-events only — leaving layout alone so the widget's own
        // open/close behaviour is untouched. It stays clickable to script.
        var HIDE_CSS =
          '.activator,[part~="activator"],.activator-nudge{' +
          'opacity:0!important;pointer-events:none!important;}';

        var hideNativeLauncher = function (activator) {
          var owner = activator.getRootNode();
          if (!owner || !owner.appendChild) return;
          if (owner.querySelector('style[data-s2-chat]')) return;
          var style = document.createElement('style');
          style.setAttribute('data-s2-chat', '');
          style.textContent = HIDE_CSS;
          owner.appendChild(style);
        };

        /* Inbox tears down and rebuilds its runtime element as the user opens
           and closes chat, which discards anything injected into the old shadow
           root. So this re-asserts on every tick rather than adopting once —
           otherwise the native launcher quietly reappears next to our pill.

           Row visibility tracks the launcher too, so we never present a Live
           chat option that has nothing to click. */
        var syncChat = function () {
          var activator = findActivator();
          if (activator) {
            hideNativeLauncher(activator);
            document.documentElement.classList.add('s2-chat-merged');
            chatRow.hidden = false;
            return;
          }
          // No launcher to drive — the widget may be absent, still booting, or
          // suppressed by its own rules. Either way, stand down: never leave a
          // Live chat row on screen that has nothing behind it.
          document.documentElement.classList.remove('s2-chat-merged');
          chatRow.hidden = true;
        };

        syncChat();
        window.setInterval(syncChat, 1000);

        // The host element itself is stable even when its contents are
        // replaced, so it is the right thing to watch.
        var chatHost = document.querySelector(chatSelector);
        if (chatHost && chatHost.shadowRoot && typeof MutationObserver !== 'undefined') {
          new MutationObserver(syncChat).observe(chatHost.shadowRoot, {
            childList: true,
            subtree: true
          });
        }

        chatRow.addEventListener('click', function () {
          var activator = findActivator();
          if (!activator) return;
          // Close our sheet first so it cannot sit over the chat window.
          sheetOverlay.close();
          window.setTimeout(function () { activator.click(); }, 60);
        });
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); });
  } else {
    init();
  }

  // Theme editor re-renders sections in place.
  document.addEventListener('shopify:section:load', function (event) { init(event.target); });

  // Keep badges honest when the theme's cart JS updates.
  document.addEventListener('cart:updated', refreshCartCount);
  document.addEventListener('cart:refresh', refreshCartCount);
})();

